import { create } from 'zustand'
import { getAxiosInstance } from '../network/axiosInstance'

interface Organization {
  id: string
  name: string
  owner: string
}

interface File {
  id: string
  filename: string
  url: string
  contentType: string
  size: number
  createdAt: string
}

interface Settings {
  releaseNotesHiddenDate: string | null
}

interface AccountState {
  id: string | null
  email: string | null
  name: string | null
  role: string | null
  title: string | null
  primaryOrganization: Organization | null
  organizations: Organization[]
  files: File[]
  settings: Settings
  isLoading: boolean
  error: string | null

  // Actions
  fetchAccountInfo: () => Promise<void>
  updateAccountInfo: (data: { name: string; role?: string; title?: string }) => Promise<void>
  updateReleaseNotesHiddenDate: (date: string) => Promise<void>
}

export const useAccountStore = create<AccountState>((set) => ({
  id: null,
  email: null,
  name: null,
  role: null,
  title: null,
  primaryOrganization: null,
  organizations: [],
  files: [],
  settings: {
    releaseNotesHiddenDate: null
  },
  isLoading: false,
  error: null,

  fetchAccountInfo: async () => {
    try {
      set({ isLoading: true, error: null })

      const response = await getAxiosInstance().get('api/account')
      console.log('Server response:', response.data); // Log server data

      set({
        id: response.data.id,
        email: response.data.email,
        name: response.data.name,
        role: response.data.role,
        title: response.data.title,
        primaryOrganization: response.data.primaryOrganization,
        organizations: response.data.organizations || [],
        files: response.data.files || [],
        settings: response.data.settings || { releaseNotesHiddenDate: null }, // Ensure settings is set
        isLoading: false
      })
    } catch (error) {
      console.error('Error fetching account info:', error)
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch account information',
        isLoading: false
      })
    }
  },

  updateAccountInfo: async (data) => {
    try {
      set({ isLoading: true, error: null })

      const response = await getAxiosInstance().put('api/account', data)

      set({
        name: response.data.name,
        role: response.data.role,
        title: response.data.title,
        isLoading: false
      })
    } catch (error) {
      console.error('Error updating account info:', error)
      set({
        error: error instanceof Error ? error.message : 'Failed to update account information',
        isLoading: false
      })
    }
  },

  updateReleaseNotesHiddenDate: async (date: string) => {
    try {
      set({ isLoading: true, error: null })
      await getAxiosInstance().put('api/account/releaseNotesHiddenDate', {
        releaseNotesHiddenDate: date
      })
      set((state) => ({
        settings: { ...state.settings, releaseNotesHiddenDate: date },
        isLoading: false
      }))
    } catch (error) {
      console.error('Error updating release notes hidden date:', error)
      set({
        error: 'Failed to update release notes hidden date',
        isLoading: false
      })
    }
  }
}))